/**
 * The well, and every operation that changes its shape.
 *
 * Two of the bugs in `docs/KNOWN-BUGS.md` live in this file's subject matter:
 *
 * - **Item 1, row removal order.** `clearRows` splices the highest row index
 *   first. Splicing ascending indices shifts the array under the next splice,
 *   so a double, triple or quad removes the wrong rows.
 * - **Item 2, group gravity is not column gravity.** `settleGroups` finds
 *   four-connected groups and drops each one as a rigid unit. Compacting each
 *   column on its own would fill every hole and make the chain mode trivial.
 */
import type { Board, Cell, Vec2 } from './types.js';

/** A fresh row of empty cells. */
export function emptyRow(width: number): Cell[] {
  return new Array<Cell>(width).fill(null);
}

/** A garbage row with a single hole. A hole outside the well means no hole. */
export function garbageRow(width: number, holeColumn: number): Cell[] {
  const row = new Array<Cell>(width).fill('G');
  if (holeColumn >= 0 && holeColumn < width) row[holeColumn] = null;
  return row;
}

/** An empty well of `hiddenRows + visibleRows` rows. */
export function createBoard(width: number, visibleRows: number, hiddenRows: number): Board {
  const height = visibleRows + hiddenRows;
  const rows: Cell[][] = [];
  for (let i = 0; i < height; i++) rows.push(emptyRow(width));
  return { width, height, hiddenRows, visibleRows, rows };
}

/** Deep copy. The clone shares nothing mutable with the original. */
export function cloneBoard(board: Board): Board {
  return {
    width: board.width,
    height: board.height,
    hiddenRows: board.hiddenRows,
    visibleRows: board.visibleRows,
    rows: board.rows.map((row) => [...row]),
  };
}

/** The cell at `(x, y)`, or `null` when the coordinates are off the board. */
export function cellAt(board: Board, x: number, y: number): Cell {
  const row = board.rows[y];
  if (row === undefined) return null;
  return row[x] ?? null;
}

/** Write a cell. Off-board coordinates are ignored. */
export function setCell(board: Board, x: number, y: number, value: Cell): void {
  const row = board.rows[y];
  if (row === undefined || x < 0 || x >= board.width) return;
  row[x] = value;
}

/**
 * Whether a piece cell may not occupy `(x, y)`.
 *
 * The walls, the floor and the ceiling of the buffer all block. The buffer is
 * finite on purpose: a bounded board is what makes the state hashable.
 */
export function isBlocked(board: Board, x: number, y: number): boolean {
  if (x < 0 || x >= board.width) return true;
  if (y < 0 || y >= board.height) return true;
  return cellAt(board, x, y) !== null;
}

/** Row indices that are completely full, top first. Hidden rows count. */
export function fullRows(board: Board): number[] {
  const full: number[] = [];
  for (const [y, row] of board.rows.entries()) {
    if (row.every((cell) => cell !== null)) full.push(y);
  }
  return full;
}

/** True when not one cell anywhere — hidden rows included — is occupied. */
export function isBoardEmpty(board: Board): boolean {
  return board.rows.every((row) => row.every((cell) => cell === null));
}

/**
 * Remove the given rows and push the same number of empty rows in at the top.
 *
 * `docs/KNOWN-BUGS.md` item 1: the sort is the whole point. Splicing index 3
 * before index 7 moves row 7 to index 6, and the second splice then removes a
 * row that was never full. Highest index first is the only safe order, and it
 * is the only order that leaves `rows.length` unchanged in the bargain.
 */
export function clearRows(board: Board, rows: readonly number[]): void {
  const descending = [...rows].sort((a, b) => b - a);
  for (const y of descending) board.rows.splice(y, 1);
  for (let i = 0; i < descending.length; i++) board.rows.unshift(emptyRow(board.width));
}

/** One four-connected group of occupied cells. */
export interface CellGroup {
  /** Position in the returned list, in discovery order. */
  readonly index: number;
  /** Member cells as `[x, y]`. */
  readonly cells: readonly Vec2[];
}

/**
 * Four-connected components of the occupied cells.
 *
 * Four-connectivity, not eight: two blocks touching only at a corner are two
 * groups and fall independently. That is what makes an overhang collapse into
 * something new instead of sliding down as one slab.
 */
export function connectedGroups(board: Board): readonly CellGroup[] {
  const seen = new Set<number>();
  const groups: CellGroup[] = [];
  const key = (x: number, y: number): number => y * board.width + x;

  for (const [y, row] of board.rows.entries()) {
    for (const [x, cell] of row.entries()) {
      if (cell === null || seen.has(key(x, y))) continue;

      const index = groups.length;
      const cells: Vec2[] = [];
      const stack: Vec2[] = [[x, y]];
      seen.add(key(x, y));

      let current = stack.pop();
      while (current !== undefined) {
        const [cx, cy] = current;
        cells.push(current);
        const neighbours: readonly Vec2[] = [
          [cx, cy + 1],
          [cx, cy - 1],
          [cx + 1, cy],
          [cx - 1, cy],
        ];
        for (const [nx, ny] of neighbours) {
          if (nx < 0 || nx >= board.width || ny < 0 || ny >= board.height) continue;
          if (cellAt(board, nx, ny) === null || seen.has(key(nx, ny))) continue;
          seen.add(key(nx, ny));
          stack.push([nx, ny]);
        }
        current = stack.pop();
      }

      groups.push({ index, cells });
    }
  }

  return groups;
}

/** How far a group can fall before something that is not itself stops it. */
export function groupFallDistance(board: Board, group: CellGroup): number {
  const own = new Set<number>();
  for (const [x, y] of group.cells) own.add(y * board.width + x);

  let distance = 0;
  for (;;) {
    let clear = true;
    for (const [x, y] of group.cells) {
      const target = y + distance + 1;
      if (target >= board.height) {
        clear = false;
        break;
      }
      if (cellAt(board, x, target) !== null && !own.has(target * board.width + x)) {
        clear = false;
        break;
      }
    }
    if (!clear) return distance;
    distance += 1;
  }
}

function lowestRow(group: CellGroup): number {
  let lowest = -1;
  for (const [, y] of group.cells) {
    if (y > lowest) lowest = y;
  }
  return lowest;
}

function dropGroup(board: Board, group: CellGroup, distance: number): void {
  const carried = group.cells.map(([x, y]): [number, number, Cell] => [x, y, cellAt(board, x, y)]);
  for (const [x, y] of group.cells) setCell(board, x, y, null);
  for (const [x, y, value] of carried) setCell(board, x, y + distance, value);
}

/**
 * Let four-connected groups fall as rigid units until nothing can move.
 *
 * This is the optional resolution step: only a mode with `groupGravity` on
 * calls it. One group moves per pass and the components are recomputed after
 * each move, because a group that lands can merge with what it lands on, and
 * the merged unit is what falls next.
 *
 * Returns true when anything moved.
 */
export function settleGroups(board: Board): boolean {
  let movedAny = false;
  // Every pass either moves one group to rest or stops. A group at rest can
  // only move again after merging with another, so the number of moves is
  // bounded by the cell count; this guard is generous and exists only to make
  // the loop provably finite to a reader.
  const guard = board.width * board.height * 2 + 8;

  for (let pass = 0; pass < guard; pass++) {
    const ordered = [...connectedGroups(board)].sort((a, b) => lowestRow(b) - lowestRow(a));

    let moved = false;
    for (const group of ordered) {
      const distance = groupFallDistance(board, group);
      if (distance > 0) {
        dropGroup(board, group, distance);
        moved = true;
        movedAny = true;
        break;
      }
    }
    if (!moved) break;
  }

  return movedAny;
}

/** True when at least one group is still able to fall. */
export function hasFloatingGroup(board: Board): boolean {
  return connectedGroups(board).some((group) => groupFallDistance(board, group) > 0);
}

/**
 * Push one garbage row in at the bottom, moving the whole stack up one row.
 *
 * Returns true when the top row held something, which the caller reads as a
 * top-out: the stack has been pushed out through the ceiling.
 */
export function pushGarbageRow(board: Board, holeColumn: number): boolean {
  const overflow = board.rows.slice(0, 1).some((row) => row.some((cell) => cell !== null));
  board.rows.shift();
  board.rows.push(garbageRow(board.width, holeColumn));
  return overflow;
}
